/**
 * Whisper.cpp WASM STT engine — single-threaded build, runs on any embedding
 * page (no SharedArrayBuffer / COOP+COEP required; see tools/whisper-wasm).
 *
 * Pipeline: MediaStream → AudioWorklet (ScriptProcessor fallback) → 16kHz
 * mono Float32 → energy endpointer (stt/pcm.ts) → on commit, transcribe in a
 * Web Worker (blob bootstrap + importScripts of libwhisper-st.js) → onFinal.
 *
 * P0 gate requirement baked in: ALWAYS cap the encoder context proportional
 * to the utterance (audio_ctx = min(1500, ceil(sec/30*1500)+64)) — the fixed
 * 30s window otherwise costs ~23s per utterance regardless of length
 * (tools/whisper-wasm/RESULTS.md).
 */
import type { SttEngine, SttEngineOpts, SttSession, SttState } from "./engine";
import { createEndpointer, downsampleTo16k, normalizeUtterance, rms, WHISPER_SAMPLE_RATE } from "./pcm";
import { stripHallucinations, collapseRepeatedPhrases } from "./text";

export type WhisperAssets = {
  /** Absolute URL of the single-file runtime (libwhisper-st.js). */
  runtimeUrl: string;
  /** Absolute URL of the ggml model (e.g. ggml-tiny.en-q5_1.bin). */
  modelUrl: string;
  /** Cache API bucket for the model (one download per origin). */
  cacheName?: string;
};

export type WhisperLoadProgress =
  | { phase: "fetching_model"; receivedBytes: number; totalBytes: number | null }
  | { phase: "initializing" }
  | { phase: "ready" };

/** Worker protocol (module-internal contract; PCM transfers as ArrayBuffer). */
type WorkerInMsg =
  | { type: "boot"; runtimeUrl: string }
  | { type: "model"; model: ArrayBuffer }
  | { type: "transcribe"; id: number; pcm: ArrayBuffer; lang: string };
type WorkerOutMsg =
  | { type: "booted" }
  | { type: "loaded" }
  | { type: "load_failed"; error: string }
  | { type: "result"; id: number; text: string }
  | { type: "error"; id: number; error: string };

/**
 * Worker source, stringified into a blob URL — the widget's IIFE bundles
 * can't emit worker entry points, and a blob worker inherits our script's
 * origin rules while importScripts pulls the runtime cross-origin.
 * NOTE: plain JS inside the string — it never passes through tsc.
 */
const WORKER_SOURCE = `
let inst = 0;
const lines = [];
self.Module = {
  print: (t) => { if (/\\]/.test(t)) lines.push(String(t).replace(/^\\[[^\\]]*\\]\\s*/, "").trim()); },
  printErr: () => {},
};
self.onmessage = (ev) => {
  const msg = ev.data;
  if (msg.type === "boot") {
    // Compile the wasm runtime IMMEDIATELY — in parallel with the model
    // download on the main thread and whatever audio is playing.
    try {
      importScripts(msg.runtimeUrl);
      const ready = () => self.postMessage({ type: "booted" });
      if (Module.calledRun) ready();
      else Module.onRuntimeInitialized = ready;
    } catch (e) {
      self.postMessage({ type: "load_failed", error: String(e) });
    }
    return;
  }
  if (msg.type === "model") {
    try {
      Module.FS_createDataFile("/", "whisper.bin", new Uint8Array(msg.model), true, true);
      inst = Module.init("whisper.bin");
      if (!inst) throw new Error("whisper init returned 0");
      self.postMessage({ type: "loaded" });
    } catch (e) {
      self.postMessage({ type: "load_failed", error: String(e) });
    }
    return;
  }
  if (msg.type === "transcribe") {
    try {
      const pcm = new Float32Array(msg.pcm);
      const sec = pcm.length / ${WHISPER_SAMPLE_RATE};
      const audioCtx = Math.min(1500, Math.ceil((sec / 30) * 1500) + 64);
      lines.length = 0;
      const rc = Module.full_default(inst, pcm, msg.lang, 1, false, audioCtx);
      if (rc) throw new Error("full_default rc=" + rc);
      const text = lines.join(" ").replace(/\\s+/g, " ").trim();
      self.postMessage({ type: "result", id: msg.id, text });
    } catch (e) {
      self.postMessage({ type: "error", id: msg.id, error: String(e) });
    }
  }
};
`;

/** Fetch the model with Cache API + progress. */
async function fetchModel(
  url: string,
  cacheName: string,
  onProgress: (p: WhisperLoadProgress) => void,
): Promise<ArrayBuffer> {
  let cache: Cache | null = null;
  try {
    cache = await caches.open(cacheName);
    const hit = await cache.match(url);
    if (hit) return await hit.arrayBuffer();
  } catch {
    cache = null; // Cache API unavailable (e.g. some private modes) — stream anyway
  }

  const res = await fetch(url);
  if (!res.ok) throw new Error(`model fetch ${res.status}`);
  const totalBytes = Number(res.headers.get("content-length")) || null;

  if (!res.body) {
    const buf = await res.arrayBuffer();
    if (cache) await cache.put(url, new Response(buf.slice(0))).catch(() => {});
    return buf;
  }
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let receivedBytes = 0;
  // Throttle progress: a fast (localhost/CDN) fetch delivers hundreds of
  // chunks per second — unthrottled callbacks caused a main-thread render
  // storm that stalled host-audio playback for seconds (bench catch).
  let lastEmit = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    receivedBytes += value.length;
    const now = Date.now();
    if (now - lastEmit >= 250) {
      lastEmit = now;
      onProgress({ phase: "fetching_model", receivedBytes, totalBytes });
    }
  }
  const buf = new Uint8Array(receivedBytes);
  let off = 0;
  for (const c of chunks) {
    buf.set(c, off);
    off += c.length;
  }
  if (cache) {
    await cache.put(url, new Response(buf.slice().buffer)).catch(() => {});
  }
  return buf.buffer;
}

export type WhisperRuntime = {
  /** Warm the worker + model (idempotent). Resolves when transcription-ready. */
  load: (onProgress?: (p: WhisperLoadProgress) => void) => Promise<void>;
  /** Transcribe one 16kHz mono utterance. */
  transcribe: (pcm: Float32Array, lang: string) => Promise<string>;
  dispose: () => void;
};

/** One worker + one loaded model, shared across engine sessions. */
export function createWhisperRuntime(assets: WhisperAssets): WhisperRuntime {
  let worker: Worker | null = null;
  let loadPromise: Promise<void> | null = null;
  let nextId = 1;
  const pending = new Map<number, { resolve: (t: string) => void; reject: (e: Error) => void }>();

  const load = (onProgress?: (p: WhisperLoadProgress) => void): Promise<void> => {
    if (loadPromise) return loadPromise;
    loadPromise = (async () => {
      // Spawn + boot the worker FIRST: wasm compile runs in parallel with the
      // model download (and with any host audio playing). The two halves join
      // at the "model" post below.
      const blob = new Blob([WORKER_SOURCE], { type: "text/javascript" });
      const blobUrl = URL.createObjectURL(blob);
      try {
        worker = new Worker(blobUrl);
      } finally {
        URL.revokeObjectURL(blobUrl);
      }
      worker.onmessage = (ev: MessageEvent) => {
        const msg = ev.data as WorkerOutMsg;
        if (msg.type === "result" || msg.type === "error") {
          const p = pending.get(msg.id);
          if (!p) return;
          pending.delete(msg.id);
          if (msg.type === "result") p.resolve(msg.text);
          else p.reject(new Error(msg.error));
        }
      };
      const waitFor = (want: "booted" | "loaded"): Promise<void> =>
        new Promise<void>((resolve, reject) => {
          const w = worker;
          if (!w) return reject(new Error("worker gone"));
          const onMsg = (ev: MessageEvent) => {
            const msg = ev.data as WorkerOutMsg;
            if (msg.type === want) {
              w.removeEventListener("message", onMsg);
              resolve();
            } else if (msg.type === "load_failed") {
              w.removeEventListener("message", onMsg);
              reject(new Error(msg.error));
            }
          };
          w.addEventListener("message", onMsg);
        });

      const booted = waitFor("booted");
      const bootMsg: WorkerInMsg = { type: "boot", runtimeUrl: assets.runtimeUrl };
      worker.postMessage(bootMsg);

      const model = await fetchModel(
        assets.modelUrl,
        assets.cacheName ?? "bhai-stt-v1",
        onProgress ?? (() => {}),
      );
      await booted;
      onProgress?.({ phase: "initializing" });
      const loaded = waitFor("loaded");
      const w = worker;
      if (!w) throw new Error("worker gone");
      const modelMsg: WorkerInMsg = { type: "model", model };
      w.postMessage(modelMsg, [model]);
      await loaded;
      onProgress?.({ phase: "ready" });
    })();
    loadPromise = loadPromise.catch((e) => {
      loadPromise = null; // allow retry after a failure
      throw e;
    });
    return loadPromise;
  };

  return {
    load,
    async transcribe(pcm, lang) {
      await load();
      const w = worker;
      if (!w) throw new Error("runtime disposed");
      const id = nextId++;
      // Copy into a fresh (guaranteed non-shared) ArrayBuffer for transfer.
      const copy = new Float32Array(pcm.length);
      copy.set(pcm);
      const buf = copy.buffer;
      return new Promise<string>((resolve, reject) => {
        pending.set(id, { resolve, reject });
        const inMsg: WorkerInMsg = { type: "transcribe", id, pcm: buf, lang };
        w.postMessage(inMsg, [buf]);
      });
    },
    dispose() {
      worker?.terminate();
      worker = null;
      loadPromise = null;
      for (const [, p] of pending) p.reject(new Error("disposed"));
      pending.clear();
    },
  };
}

/**
 * The SttEngine: capture from the given MediaStream, endpoint locally,
 * transcribe committed utterances on the shared runtime.
 */
export function createWhisperEngine(runtime: WhisperRuntime): SttEngine {
  return (opts: SttEngineOpts): SttSession => {
    let active = true;
    let audioCtx: AudioContext | null = null;
    let cleanupCapture: (() => void) | null = null;
    const endpointer = createEndpointer({ silenceMs: opts.silenceMs ?? 1600 });
    const lang = (opts.lang ?? "en").slice(0, 2);
    let busy = false;
    let stopping = false;
    const pendingCommits: Float32Array[] = [];

    const finish = (state: SttState) => {
      if (!active) return;
      active = false;
      cleanupCapture?.();
      void audioCtx?.close().catch(() => {});
      opts.onState(state);
    };

    /** Whisper emits sound-effect hallucinations on marginal audio —
     *  "(keyboard clicking)", "[BLANK_AUDIO]", "♪" — never real dictation. */

    const handleCommit = (pcmIn: Float32Array) => {
      let pcm = pcmIn;
      if (busy) {
        // NEVER drop speech: queue behind the in-flight transcription.
        pendingCommits.push(pcm);
        return;
      }
      busy = true;
      // Tell the caller its listen window must not abort us now — the visitor
      // has already spoken; these words exist and are mid-flight.
      opts.onTranscribing?.(true);
      const t0 = Date.now();
      // Gain-normalize: whisper handles quiet mics far better at ~0.08 RMS.
      pcm = normalizeUtterance(pcm);
      runtime
        .transcribe(pcm, lang)
        .then((text) => {
          busy = false;
          if (pendingCommits.length === 0) opts.onTranscribing?.(false);
          if (!active) return;
          const t = collapseRepeatedPhrases(stripHallucinations(text));
          try {
            console.log(
              `[BlueHat stt] utterance ${(pcm.length / 16000).toFixed(1)}s → ${Date.now() - t0}ms: ${t ? `"${t.slice(0, 80)}"` : "(empty)"}${text !== t ? ` [raw: ${text.slice(0, 60)}]` : ""}`,
            );
          } catch { /* ignore */ }
          const next = pendingCommits.shift();
          if (t) {
            active = false;
            cleanupCapture?.();
            void audioCtx?.close().catch(() => {});
            opts.onState("idle");
            opts.onFinal(t);
          } else if (stopping) {
            // Push-to-talk release whose audio transcribed to nothing: the
            // capture is already torn down — idle NOW, never linger red.
            finish("idle");
          } else {
            opts.onInterim?.("");
            if (next) handleCommit(next);
          }
        })
        .catch((err: unknown) => {
          try { console.warn("[BlueHat stt] transcribe failed:", String(err)); } catch { /* ignore */ }
          busy = false;
          opts.onTranscribing?.(false);
          finish("error");
        });
    };

    let lastLevelAt = 0;
    const onFrame = (frame: Float32Array, sampleRate: number) => {
      if (!active) return;
      const pcm16k = downsampleTo16k(frame, sampleRate);
      // Audio-reactive mic glow: throttled (~80ms) so it can never become a
      // render storm; 0.15 RMS ≈ loud speech → full ring.
      if (opts.onLevel) {
        const now = Date.now();
        if (now - lastLevelAt >= 80) {
          lastLevelAt = now;
          // /0.05: quiet-mic speech should still visibly move the ring.
          opts.onLevel(Math.min(1, rms(pcm16k) / 0.05));
        }
      }
      for (const ev of endpointer.push(pcm16k)) {
        if (ev.type === "commit") handleCommit(ev.pcm);
        else if (ev.type === "blip") {
          try {
            console.log(
              `[BlueHat stt] dropped ${Math.round(ev.speechMs)}ms blip (min ${160}ms, threshold ${ev.threshold.toFixed(4)})`,
            );
          } catch { /* ignore */ }
        } else if (ev.type === "silent_window") opts.onInterim?.("");
        else if (ev.type === "speech_started") opts.onInterim?.("…");
      }
    };

    // ── Capture: AudioWorklet, ScriptProcessor fallback ──────────────────
    void (async () => {
      try {
        audioCtx = new AudioContext();
        // A context created outside a direct user gesture can start
        // "suspended" — no frames would ever flow. The visitor's AI Voice tap
        // gives the page activation, so resume() succeeds here.
        if (audioCtx.state === "suspended") {
          await audioCtx.resume().catch(() => {});
        }
        const source = audioCtx.createMediaStreamSource(opts.stream);
        const rate = audioCtx.sampleRate;
        // The graph is only RENDERED (and process()/onaudioprocess only
        // called) when it reaches destination. Route through a zero-gain node
        // so the tap runs without any audible output.
        const sink = audioCtx.createGain();
        sink.gain.value = 0;
        sink.connect(audioCtx.destination);

        let workletOk = false;
        if (audioCtx.audioWorklet) {
          try {
            const workletSrc = `
              registerProcessor("bhai-tap", class extends AudioWorkletProcessor {
                process(inputs) {
                  const ch = inputs[0] && inputs[0][0];
                  if (ch) this.port.postMessage(ch.slice(0));
                  return true;
                }
              });`;
            const url = URL.createObjectURL(new Blob([workletSrc], { type: "text/javascript" }));
            await audioCtx.audioWorklet.addModule(url);
            URL.revokeObjectURL(url);
            const node = new AudioWorkletNode(audioCtx, "bhai-tap");
            node.port.onmessage = (ev: MessageEvent) => onFrame(ev.data as Float32Array, rate);
            source.connect(node);
            node.connect(sink); // pull the chain — unconnected worklets never run
            cleanupCapture = () => {
              node.port.onmessage = null;
              try { source.disconnect(node); node.disconnect(); } catch { /* ignore */ }
            };
            workletOk = true;
          } catch {
            workletOk = false; // CSP can block blob worklets — fall back
          }
        }
        if (!workletOk) {
          const proc = audioCtx.createScriptProcessor(4096, 1, 1);
          proc.onaudioprocess = (ev) => onFrame(new Float32Array(ev.inputBuffer.getChannelData(0)), rate);
          source.connect(proc);
          proc.connect(sink);
          cleanupCapture = () => {
            proc.onaudioprocess = null;
            try { source.disconnect(proc); proc.disconnect(); } catch { /* ignore */ }
          };
        }
        opts.onState("listening");
      } catch (err) {
        try { console.warn("[BlueHat stt] capture setup failed:", String(err)); } catch { /* ignore */ }
        finish("error");
      }
    })();

    return {
      stop() {
        if (!active) return;
        const events = endpointer.flush();
        const commit = events.find((e) => e.type === "commit");
        if (commit && "pcm" in commit) {
          // Push-to-talk release: transcribe what was said; the stopping flag
          // guarantees the session resolves (onFinal OR idle) — it must never
          // sit red waiting for commits that can no longer arrive.
          stopping = true;
          handleCommit(commit.pcm);
          cleanupCapture?.();
        } else {
          finish("idle");
        }
      },
      abort() {
        endpointer.reset();
        finish("idle");
      },
    };
  };
}
